import clsx from "clsx";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { RateRadioButton } from "@/apps/partner/components/RateRadioButton/RateRadioButton";
import { Subtract } from "@/assets/icons/icons";
import { FreeEsimTrialTooltip } from "@/components/Onboarding/Steps/RateSelection/FreeEsimTrialTooltip/FreeEsimTrialTooltip";
import { RateOptionPropsBase } from "@/types";

const rateStyle = "flex-1 flex-col w-full py-4 pl-4";
const daysLabelStyle = "ml-7 text-xs text-primary-100";

export const RateOptionMobile: FC<RateOptionPropsBase> = ({
  name,
  monthOption,
  preSelectedProcessedProduct,
  isSelected,
  currentProductId,
  readonlyProductId,
  shouldShowEsimTrial = false,
  disabled,
  voice,
}) => {
  const { t } = useTranslation();

  const isRecommended = monthOption?.recommended;
  const isPrecharged = preSelectedProcessedProduct?.precharged;
  const monthIsPreselected =
    monthOption?.id === preSelectedProcessedProduct?.id ||
    monthOption?.voice === preSelectedProcessedProduct?.id;

  const label =
    isRecommended && !isPrecharged
      ? t("Onboarding.sections.rate-selection.recommended")
      : isPrecharged
        ? t("Onboarding.sections.rate-selection.precharged")
        : null;

  return (
    <div
      className={clsx(
        "relative mt-6 flex flex-col border-1 shadow-primary",
        isSelected && "border-solid border-highlight-100 bg-subtle-selected",
        disabled && !isSelected && "disabled",
        voice && "voice",
        isPrecharged && "precharged",
      )}
    >
      {label && (
        <>
          <div className="absolute left-8 top-[2px] z-10 -ml-2 text-[10px] font-semibold leading-3 text-white">
            {label}
          </div>
          <div className="hint">
            <Subtract />
          </div>
        </>
      )}
      <div
        className="content-baseline border-b-1 border-primary-50 p-4 text-base leading-6"
        data-testid="rate-descriptions"
      >
        <div className="flex gap-6">
          <span className="font-semibold text-primary-100">{name}</span>
          <span
            className={clsx(isSelected && "font-semibold text-primary-100")}
          >
            {monthOption?.bandwidthLabel} Mbit/s
          </span>
        </div>
        {shouldShowEsimTrial && currentProductId === monthOption.id && (
          <p className="flex text-primary-100">
            <span className="mr-2">
              {t("Onboarding.sections.rate-selection.esim-trial-hint")}
            </span>
            <span className="mt-1">
              <FreeEsimTrialTooltip leftOnMobile={true} />
            </span>
          </p>
        )}
      </div>
      <div className="flex">
        <div
          data-testid="rate-durations"
          className={clsx(
            rateStyle,
            isPrecharged && !monthIsPreselected && "disabled",
          )}
        >
          {monthOption && (
            <RateRadioButton
              isReadonly={readonlyProductId === monthOption?.id}
              transformedProduct={monthOption}
              disabled={isPrecharged && !monthIsPreselected}
              preSelectedProcessedProduct={preSelectedProcessedProduct}
              isSelected={isSelected}
            />
          )}
          <div className={daysLabelStyle}>
            {t("Onboarding.sections.rate-selection.30days")}
          </div>
        </div>
        <div className="flex border-r-1 border-primary-50" />
      </div>
    </div>
  );
};
